import React, { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Loader2, Stethoscope, CheckCircle2, AlertTriangle, XCircle, MinusCircle, Mail, ChevronDown } from 'lucide-react';
import { runAiqHealthCheck } from '../../lib/api';
import { errMsg, when } from './aiqMeta';

const ICONS = {
  pass: [CheckCircle2, 'text-emerald-600'], warn: [AlertTriangle, 'text-amber-600'],
  fail: [XCircle, 'text-red-600'], skipped: [MinusCircle, 'text-zinc-400'],
};

const StepRow = ({ step }) => {
  const [Icon, tone] = ICONS[step.status] || ICONS.skipped;
  return (
    <li className="flex items-start gap-3 py-2" data-testid={`aiq-check-step-${step.key}`} data-status={step.status}>
      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${tone}`} />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-zinc-900">{step.label}</div>
        <div className="text-xs text-zinc-600 break-words">{step.detail}</div>
      </div>
      {step.ms > 0 && <span className="font-mono text-[10px] text-zinc-400 shrink-0">{step.ms} ms</span>}
    </li>
  );
};

const Summary = ({ health }) => {
  if (!health) return <p className="text-sm text-zinc-500" data-testid="aiq-check-summary">Not run yet — press Run full check.</p>;
  const tone = health.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-red-300 bg-red-50 text-red-800';
  const who = health.trigger === 'nightly' ? 'nightly check' : `run by ${health.ran_by || '—'}`;
  return (
    <div className={`rounded-md border px-3 py-2 ${tone}`} data-testid="aiq-check-summary" data-ok={String(!!health.ok)}>
      <div className="text-sm font-semibold">{health.ok ? 'AccountsIQ is working' : 'AccountsIQ connection problem'}</div>
      <div className="text-xs mt-0.5">{health.summary} · {who} · <span className="font-mono">{when(health.ran_at)}</span></div>
    </div>
  );
};

const AlertLine = ({ alert }) => {
  if (!alert) return null;
  if (alert.last_alert_error) {
    return <p className="text-xs text-red-700 flex items-center gap-1.5" data-testid="aiq-alert-line"><Mail className="w-3.5 h-3.5" />Last alert email couldn&apos;t be sent: {alert.last_alert_error}</p>;
  }
  return (
    <p className="text-xs text-zinc-500 flex items-center gap-1.5" data-testid="aiq-alert-line">
      <Mail className="w-3.5 h-3.5" />
      {alert.escalation_email
        ? `Runs every night; if it fails, an email goes to ${alert.escalation_email}.${alert.last_alert_sent_at ? ` Last alert sent ${when(alert.last_alert_sent_at)}.` : ''}`
        : 'Runs every night. Add an alert email under Automation to be emailed when it fails.'}
    </p>
  );
};

export const HealthCheckPanel = ({ health, onRan, alert, collapsible = false }) => {
  const [running, setRunning] = useState(false);
  const [open, setOpen] = useState(!collapsible);
  const showSteps = health?.steps && (open || health.ok === false);

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await runAiqHealthCheck();
      if (data.ok) toast.success(data.summary); else toast.error(data.summary);
      onRan?.(data);
    } catch (e) {
      toast.error(errMsg(e, 'The connection check could not run'));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="rounded-lg border bg-white p-4 space-y-3" data-testid="aiq-health-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-semibold flex items-center gap-2"><Stethoscope className="w-4 h-4 text-[#005F73]" />Connection check</div>
          <p className="text-xs text-zinc-500 mt-0.5 max-w-xl">Signs in, reads customers, products, prices, tax codes and currencies, and checks draft-invoice access. Nothing is written to AccountsIQ.</p>
        </div>
        <div className="flex gap-2">
          {collapsible && health?.steps && health.ok && (
            <Button variant="ghost" size="sm" onClick={() => setOpen((o) => !o)} data-testid="aiq-check-toggle">
              {open ? 'Hide steps' : 'Show steps'}<ChevronDown className={`w-4 h-4 ml-1 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
            </Button>
          )}
          <Button size="sm" onClick={run} disabled={running} className="bg-[#005F73] hover:bg-[#0A9396]" data-testid="aiq-run-check-btn">
            {running ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Stethoscope className="w-4 h-4 mr-1.5" />}
            {running ? 'Checking…' : 'Run full check'}
          </Button>
        </div>
      </div>
      <Summary health={health} />
      {showSteps && <ul className="divide-y divide-zinc-100" data-testid="aiq-check-steps">{health.steps.map((s) => <StepRow key={s.key} step={s} />)}</ul>}
      <AlertLine alert={alert} />
    </div>
  );
};
